import { createModule } from "saga-slice";
import { call, put } from "redux-saga/effects";
import { createPurchase, filterLines, generatePdf, getPurchases, getPurchasesData, getTotalPurchasesForDateRange, getTotalPurchasesThisMonth, getTotalPurchasesThisWeek, getTotalPurchasesThisYear, getTotalPurchasesToday, search } from "../services/purchaseService";

const purchaseModule = createModule({
  name: "purchases",
  initialState: {
    isLoading: false,
    error: null,
    currentPurchase: null,
    cart: [],
    purchases: [],
    lines: [],
    totalPurchases: 0,
    grossRevenue: 0,
    netIncome: 0,
    highlightedItem: null,
    lowStockItems: [],
    totalAmountHt: 0,
    totalElements: 0, // Total number of records
    totalPages: 0, // Total number of pages
    pageNumber: 0, // Current page number
    pageSize: 10, // Size of each page
  },
  selectors: {
    getCart: state => state.purchases?.cart || [],
    getTotalAmountHt: state => state.purchases?.totalAmountHt || 0,
    getIsLoading: state => state.purchases?.isLoading || false
  },
  reducers: {
    search: (state) => {
      state.isLoading = true;
    },
    createPurchase: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    filterLines: (state) => {
      state.isLoading = true;
    },
    getPurchasesData: (state) => {
      state.isLoading = true;
    },
    getPurchasesDataSuccess: (state, action) => {
      state.isLoading = false;
      state.totalPurchases = action.totalPurchases;
      state.grossRevenue = action.grossRevenue;
      state.netIncome = action.netIncome;
      state.lowStockItems = action.lowStockItems;
    },
    getPurchasesDataFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    finishFetching: (state) => {
      state.isLoading = false;
    },
    fetchedData: (state, payload) => {
      state.isLoading = false;
      state.purchases = payload.body;
    },
    setFilteredLines: (state, action) => {
      state.lines = action;
    },
    getTotalPurchasesToday: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getTotalPurchasesThisWeek: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getTotalPurchasesThisMonth: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getTotalPurchasesThisYear: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    createPurchaseSuccess: (state) => {
      state.isLoading = false;
      state.cart = [];
      state.totalAmountHt = 0;
    },
    createPurchaseFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    generatePdfSuccess: (state) => {
      state.isLoading = false;
    },
    generatePdfFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    search: (state) => {
      state.isLoading = true;
    },
    addToCart: (state, action) => {
      const existingItem = state.cart.find(item => item.id === action.id);
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.cart.push({
          ...action,
          quantity: 1,
          stockActuel: action.stock || 0, // Ensure stockActuel is included
          price: action.buyingPrice,
          userPrice: action.userPrice
        });
      }
      // Highlight the current item being added
      state.highlightedItem = action.id;

      state.totalAmountHt = state.cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    setTotalPurchase: (state, action) => {
      state.totalPurchases = action.payload;
    },
    removeFromCart: (state, action) => {
      state.cart = state.cart.filter(item => item.id !== action);
      state.totalAmountHt = state.cart.reduce((total, item) =>
        total + (item.price * item.quantity), 0);
    },
    updateQuantity: (state, action) => {
      const item = state.cart.find(item => item.id === action.id);
      if (item) {
        item.quantity = action.quantity;
        item.price = action.price;
        item.userPrice = action.userPrice;

        // Highlight the current item being edited
        state.highlightedItem = action.id;
        state.totalAmountHt = state.cart.reduce((total, itm) => total + (itm.price * itm.quantity), 0);
      }
    },
    clearCart: (state) => {
      state.cart = [];
      state.totalAmountHt = 0;
    },
    fetchError: (state, action) => {
      state.isLoading = false;
      state.error = action || "An error occurred";
    },
  },
  sagas: (A) => ({
    *[A.search]({ payload }) {
      try {
        const { onSuccess,  startDate, endDate, query } = payload;
        const { data } = yield call(search, startDate, endDate, query);
        yield put(A.finishFetching());
        yield put(A.fetchedData(data));

        if (onSuccess) {
          yield call(onSuccess);
        }
      } catch (e) {
        console.log(e);
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
    },
    *[A.createPurchase]({ payload }) {
      try {
        console.log(payload.data)
        const { data } = yield call(createPurchase, payload.data);
        yield put(A.createPurchaseSuccess());

        if (payload.onSuccess) {
          yield call(payload.onSuccess, data);
        }
      } catch (error) {
        yield put(A.createPurchaseFailure(error.message));
        if (payload.onError) {
          yield call(payload.onError, error);
        }
      }
    },

    *[A.filterLines]({ payload }) {
      try {
        const { data } = yield call(filterLines, payload.startDate, payload.endDate, payload.query);
        yield put(A.setFilteredLines(data));
        yield put(A.finishFetching());
      } catch (error) {
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
    },
    *[A.getPurchaseData]({ payload }) {
      try {
        const { data } = yield call(getPurchasesData, payload.dateRange);
        yield put(A.getPurchasesDataSuccess(data));
      } catch (error) {
        yield put(A.getPurchasesDataFailure(error.message));
      }
    },
    *[A.fetchPurchases]({ payload }) {
      try {
        const { data } = yield call(getPurchases, payload.page, payload.size);
        yield put(A.setPurchases(data.content));
        yield put(A.setPagination({
          currentPage: payload.page,
          totalPages: data.totalPages,
          pageSize: payload.size,
          totalElements: data.totalElements
        }));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalPurchasesToday]() {
      try {
        const { data } = yield call(getTotalPurchasesToday);
        yield put(A.setTotalPurchases(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalPurchasesThisWeek]() {
      try {
        const { data } = yield call(getTotalPurchasesThisWeek);
        yield put(A.setTotalPurchases(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalPurchasesThisMonth]() {
      try {
        const { data } = yield call(getTotalPurchasesThisMonth);
        yield put(A.setTotalPurchases(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalPurchasesThisYear]() {
      try {
        const { data } = yield call(getTotalPurchasesThisYear);
        yield put(A.setTotalPurchases(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalPurchasesForDateRange]({ payload }) {
      try {
        const { data } = yield call(getTotalPurchasesForDateRange, payload.startDate, payload.endDate);
        yield put(A.setTotalPurchases(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.generatePdfRequest]({ payload }) {
      try {
        const pdfData = yield call(generatePdf, payload);
        const blob = new Blob([pdfData], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank'); // Open the PDF in a new window
        yield put(A.generatePdfSuccess());
      } catch (error) {
        yield put(A.generatePdfFailure(error.message));
      }
    },
  }),

});

export default purchaseModule;
export const purchaseActions = purchaseModule.actions;